/**
 * D-ID Avatar Configuration
 * Contains available avatars and voices for the D-ID streaming API
 */

export interface DIDAvatar {
  id: string;
  name: string;
  source_url: string;
  thumbnail_url?: string;
  description?: string;
  type: 'standard' | 'premium' | 'custom';
}

export interface DIDVoice {
  id: string;
  name: string;
  language: string;
  gender: 'male' | 'female' | 'neutral';
  provider: 'elevenlabs' | 'microsoft' | 'amazon' | 'google';
  accent?: string;
}

// Available D-ID public avatars
export const DID_AVATARS: DIDAvatar[] = [
  {
    id: 'amy',
    name: 'Amy',
    source_url: 'https://d-id-public-bucket.s3.amazonaws.com/amy.jpg',
    thumbnail_url: 'https://d-id-public-bucket.s3.amazonaws.com/amy.jpg',
    description: 'Professional female presenter',
    type: 'standard'
  },
  {
    id: 'josh',
    name: 'Josh',
    source_url: 'https://d-id-public-bucket.s3.amazonaws.com/josh_lite3.jpg',
    thumbnail_url: 'https://d-id-public-bucket.s3.amazonaws.com/josh_lite3.jpg',
    description: 'Professional male presenter',
    type: 'standard'
  },
  {
    id: 'carl',
    name: 'Carl',
    source_url: 'https://d-id-public-bucket.s3.amazonaws.com/carl.jpg',
    thumbnail_url: 'https://d-id-public-bucket.s3.amazonaws.com/carl.jpg',
    description: 'Casual male presenter',
    type: 'standard'
  },
  {
    id: 'noelle',
    name: 'Noelle',
    source_url: 'https://d-id-public-bucket.s3.us-west-2.amazonaws.com/noelle.jpeg',
    thumbnail_url: 'https://d-id-public-bucket.s3.us-west-2.amazonaws.com/noelle.jpeg',
    description: 'Professional female presenter',
    type: 'standard'
  }
];

// Voice configurations for different languages
export const DID_VOICES: DIDVoice[] = [
  // English voices
  {
    id: 'en-US-JennyNeural',
    name: 'Jenny (US)',
    language: 'en-US',
    gender: 'female',
    provider: 'microsoft',
    accent: 'American'
  },
  {
    id: 'en-US-GuyNeural',
    name: 'Guy (US)',
    language: 'en-US',
    gender: 'male',
    provider: 'microsoft',
    accent: 'American'
  },
  {
    id: 'en-GB-SoniaNeural',
    name: 'Sonia (UK)',
    language: 'en-GB',
    gender: 'female',
    provider: 'microsoft',
    accent: 'British'
  },
  {
    id: 'en-GB-RyanNeural',
    name: 'Ryan (UK)',
    language: 'en-GB',
    gender: 'male',
    provider: 'microsoft',
    accent: 'British'
  },
  // Spanish voices
  {
    id: 'es-ES-ElviraNeural',
    name: 'Elvira (Spain)',
    language: 'es-ES',
    gender: 'female',
    provider: 'microsoft',
    accent: 'Spanish'
  },
  {
    id: 'es-MX-DaliaNeural',
    name: 'Dalia (Mexico)',
    language: 'es-MX',
    gender: 'female',
    provider: 'microsoft',
    accent: 'Mexican'
  },
  // French voices
  {
    id: 'fr-FR-DeniseNeural',
    name: 'Denise (France)',
    language: 'fr-FR',
    gender: 'female',
    provider: 'microsoft',
    accent: 'French'
  },
  // German voices
  {
    id: 'de-DE-KatjaNeural',
    name: 'Katja (Germany)',
    language: 'de-DE',
    gender: 'female',
    provider: 'microsoft',
    accent: 'German'
  },
  // Hindi voices
  {
    id: 'hi-IN-SwaraNeural',
    name: 'Swara (Hindi)',
    language: 'hi-IN',
    gender: 'female',
    provider: 'microsoft',
    accent: 'Indian'
  },
  // Add ElevenLabs voices
  {
    id: '21m00Tcm4TlvDq8ikWAM',
    name: 'Rachel',
    language: 'en-US',
    gender: 'female',
    provider: 'elevenlabs',
    accent: 'American'
  },
  {
    id: 'AZnzlk1XvdvUeBnXmlld',
    name: 'Domi',
    language: 'en-US',
    gender: 'female',
    provider: 'elevenlabs',
    accent: 'American'
  }
];

// Get voice for a specific language
export const getVoiceForLanguage = (languageCode: string, gender?: 'male' | 'female'): DIDVoice | undefined => {
  const voices = DID_VOICES.filter(v => v.language === languageCode);
  if (gender) {
    const genderVoice = voices.find(v => v.gender === gender);
    if (genderVoice) return genderVoice;
  }
  return voices[0];
};

// Get default avatar
export const getDefaultAvatar = (): DIDAvatar => {
  return DID_AVATARS[0]; // Amy as default
};

// D-ID API Configuration
export const DID_CONFIG = {
  // API key should be provided via headers from backend or environment
  API_KEY_HEADER: 'x-did-api-key',
  
  // Animation driver for avatar movements
  DEFAULT_DRIVER_URL: 'bank://lively/',
  
  // Stream configuration
  STREAM_CONFIG: {
    stitch: true, // Enable stitching for smoother transitions
  },
  
  // WebRTC configuration
  WEBRTC_CONFIG: {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  }
};